import { useState, useEffect } from "react";
import { useApp, api, setToken, BADGES } from "../context";
import { Shell, Btn, Avatar, Toggle, EmptyState, TierBadge, TrustBar } from "../components";
import PostCard from "../PostCard";

// ── EDIT PROFILE PAGE ──
export const EditProfilePage = ({ profileForm, setProfileForm, handleProfilePicUpload, fileRef }) => {
  const { c, go, user, setUser, flash } = useApp();
  return (
    <Shell title="Edit Profile" showBack onBack={() => go("profile")}>
      <div style={{ padding: "16px 0" }}>
        <div style={{ background: c.card, borderRadius: 16, border: `1px solid ${c.border}`, padding: 20, marginBottom: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20 }}>
            <Avatar id="me" size={64} />
            <div>
              <Btn small primary onClick={() => fileRef.current?.click()}>Change Photo</Btn>
              <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleProfilePicUpload} />
              <div style={{ fontSize: 11, color: c.faint, marginTop: 4 }}>JPG, PNG, GIF. Max 5MB.</div>
            </div>
          </div>
          {[
            { l: "DISPLAY NAME", k: "name", placeholder: "Your display name" },
            { l: "BIO", k: "bio", placeholder: "Tell people about yourself...", textarea: true },
            { l: "LOCATION", k: "location", placeholder: "Where are you based?" },
            { l: "WEBSITE", k: "website", placeholder: "https://yoursite.com" },
          ].map(f => (
            <div key={f.k} style={{ marginBottom: 14 }}>
              <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: c.sub, marginBottom: 5, letterSpacing: "0.05em" }}>{f.l}</label>
              {f.textarea
                ? <textarea style={{ width: "100%", padding: "12px 14px", borderRadius: 11, border: `1.5px solid ${c.border}`, background: c.input, color: c.text, fontSize: 14, outline: "none", fontFamily: "inherit", minHeight: 80, resize: "vertical", boxSizing: "border-box" }} value={profileForm[f.k] || ""} placeholder={f.placeholder} onChange={e => setProfileForm({ ...profileForm, [f.k]: e.target.value })} />
                : <input style={{ width: "100%", padding: "12px 14px", borderRadius: 11, border: `1.5px solid ${c.border}`, background: c.input, color: c.text, fontSize: 14, outline: "none", boxSizing: "border-box", fontFamily: "inherit" }} value={profileForm[f.k] || ""} placeholder={f.placeholder} onChange={e => setProfileForm({ ...profileForm, [f.k]: e.target.value })} />
              }
            </div>
          ))}
          <Btn primary full onClick={async () => {
            const res = await api("/profile", { method: "PUT", body: JSON.stringify({ name: profileForm.name || user.name, bio: profileForm.bio, location: profileForm.location, website: profileForm.website }) });
            if (res.ok) { setUser({ ...user, name: profileForm.name || user.name, bio: profileForm.bio, location: profileForm.location, website: profileForm.website }); flash("Profile updated! ✅"); go("profile"); }
            else flash(res.message || "Failed to update", "error");
          }}>Save Changes</Btn>
        </div>
        <div style={{ background: c.card, borderRadius: 16, border: `1px solid #ef444425`, padding: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#ef4444", marginBottom: 12 }}>⚠️ Danger Zone</div>
          <Btn danger full onClick={async () => { await api("/auth/logout", { method: "POST" }); setToken(null); setUser(null); sessionStorage.clear(); go("login"); flash("Logged out"); }}>Log Out</Btn>
          <div style={{ height: 8 }} />
          <Btn full style={{ color: "#ef4444", border: `1px solid #ef444425` }} onClick={() => flash("Contact support to delete your account", "error")}>Delete Account</Btn>
        </div>
      </div>
    </Shell>
  );
};

// ── MY PROFILE PAGE ──
export const MyProfilePage = ({ myPosts, bookmarkedPosts, profileEmoji, effectiveProfilePic, handleProfilePicUpload, fileRef }) => {
  const { c, go, user, setUser } = useApp();
  const plan = user?.plan || "free";
  const planColor = plan === "elite" ? "#f59e0b" : plan === "premium" ? "#7c6cff" : c.sub;
  return (
    <Shell title="My Profile" showBack onBack={() => go("feed")}>
      <div style={{ padding: "16px 0" }}>
        <div style={{ background: c.card, borderRadius: 20, border: `1px solid ${c.border}`, overflow: "hidden", marginBottom: 14 }}>
          <div style={{ height: 100, background: user?.coverUrl ? `url(${user.coverUrl}) center/cover` : `linear-gradient(135deg, #6c5ce7, ${planColor})`, position: "relative" }}>
            <div style={{ position: "absolute", bottom: -30, left: 18 }}>
              <div onClick={() => fileRef.current?.click()} style={{ width: 68, height: 68, borderRadius: 18, background: effectiveProfilePic ? `url(${effectiveProfilePic}) center/cover` : c.card, border: `3px solid ${c.card}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, fontWeight: 800, color: c.pri, cursor: "pointer", overflow: "hidden", position: "relative" }}>
                {!effectiveProfilePic && profileEmoji}
                <div style={{ position: "absolute", bottom: 0, right: 0, width: 22, height: 22, borderRadius: 6, background: c.pri, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: "#fff" }}>📷</div>
              </div>
              <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleProfilePicUpload} />
            </div>
          </div>
          <div style={{ padding: "38px 18px 18px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
              <h2 style={{ fontSize: 20, fontWeight: 800, margin: 0, color: c.text }}>{user?.name || "You"}</h2>
              <TierBadge plan={user?.plan} verified={user?.verified} size={16} />
            </div>
            <p style={{ fontSize: 13, color: c.sub, margin: "3px 0 12px" }}>{user?.bio || "No bio yet"}</p>
            <div style={{ display: "flex", gap: 24, marginBottom: 14 }}>
              {[{ l: "Posts", v: myPosts.length }, { l: "Followers", v: user?.followers || 0 }, { l: "Following", v: user?.following || 0 }].map(s => (
                <div key={s.l} style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 18, fontWeight: 800, color: c.text }}>{s.v}</div>
                  <div style={{ fontSize: 11, color: c.sub }}>{s.l}</div>
                </div>
              ))}
            </div>
            <TrustBar score={user?.trustScore || 0} />
            <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
              <Btn primary full onClick={() => go("edit_profile")}>Edit Profile</Btn>
              <Btn full onClick={() => go("premium")}>⭐ Premium</Btn>
            </div>
          </div>
        </div>
        {/* Posts */}
        <div style={{ fontSize: 13, fontWeight: 700, color: c.sub, marginBottom: 10, letterSpacing: "0.05em" }}>MY POSTS</div>
        {myPosts.length === 0 ? <EmptyState icon="📝" title="No posts yet" desc="Share your first thought!" /> : myPosts.map(p => <PostCard key={p.id} post={p} />)}
      </div>
    </Shell>
  );
};

// ── BOOKMARKS PAGE ──
export const BookmarksPage = ({ bookmarkedPosts }) => {
  const { c, go } = useApp();
  return (
    <Shell title="Bookmarks" showBack onBack={() => go("feed")}>
      <div style={{ padding: "16px 0" }}>
        {bookmarkedPosts.length === 0
          ? <EmptyState icon="🔖" title="No bookmarks yet" desc="Bookmark posts to save them here" />
          : bookmarkedPosts.map(p => <PostCard key={p.id} post={p} />)
        }
      </div>
    </Shell>
  );
};

// ── BADGES PAGE ──
export const BadgesPage = () => {
  const { c, go, user } = useApp();
  return (
    <Shell title="My Badges" showBack onBack={() => go("feed")}>
      <div style={{ padding: "16px 0" }}>
        <div style={{ background: c.card, borderRadius: 16, border: `1px solid ${c.border}`, padding: 16, marginBottom: 14, display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ fontSize: 28 }}>🏅</div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: c.text }}>Your Achievements</div>
            <div style={{ fontSize: 11, color: c.faint }}>Earn badges by being active in the community</div>
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {BADGES.map(b => {
            const earned = user?.badges?.includes(b.id) ||
              (b.id === 'b1' && (user?.postCount || 0) > 0) ||
              (b.id === 'b3' && (user?.trustScore || 0) >= 50) ||
              (b.id === 'b5' && user?.verified) ||
              (b.id === 'b6' && (user?.plan === 'premium' || user?.plan === 'elite')) ||
              (b.id === 'b7' && (user?.trustScore || 0) >= 90);
            return (
              <div key={b.id} style={{ background: c.card, borderRadius: 14, border: `1px solid ${earned ? "#f59e0b40" : c.border}`, padding: 16, textAlign: "center", opacity: earned ? 1 : 0.45, transition: "all 0.2s" }}>
                <div style={{ fontSize: 34, marginBottom: 8 }}>{b.icon}</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: c.text, marginBottom: 3 }}>{b.name}</div>
                <div style={{ fontSize: 10, color: c.sub }}>{b.desc}</div>
                {earned && <div style={{ fontSize: 10, fontWeight: 700, color: "#f59e0b", marginTop: 6, background: "#f59e0b15", padding: "3px 10px", borderRadius: 8, display: "inline-block" }}>✓ Earned</div>}
              </div>
            );
          })}
        </div>
      </div>
    </Shell>
  );
};

// ── ABOUT ME PAGE ──
export const AboutMePage = () => {
  const { c, go, user } = useApp();
  const plan = user?.plan || "free";
  const planColor = plan === "elite" ? "#f59e0b" : plan === "premium" ? "#7c6cff" : c.sub;
  const planLabel = plan === "elite" ? "Elite 👑" : plan === "premium" ? "Premium 💎" : "Free";
  const rows = [
    { l: "Name", v: user?.name, icon: "👤" },
    { l: "Age", v: user?.age ? `${user.age} years old` : "Not set", icon: "📅" },
    { l: "Email", v: user?.email ? user.email.replace(/(.{2}).*(@.*)/, '$1***$2') : "Not set", icon: "📧" },
    { l: "Location", v: user?.location || "Not set", icon: "📍" },
    { l: "Joined", v: user?.joinDate, icon: "🗓️" },
    { l: "Trust Score", v: `★ ${user?.trustScore || 0}`, icon: "⭐" },
    { l: "Verified", v: user?.verified ? "✓ Yes" : "Not yet", icon: "🛡️" },
    { l: "Plan", v: planLabel, icon: "💳" },
    { l: "Looking For", v: user?.lookingFor || "Not set", icon: "💝" },
  ];
  return (
    <Shell title="About Me" showBack onBack={() => go("feed")}>
      <div style={{ padding: "16px 0" }}>
        {/* Profile card */}
        <div style={{ background: `linear-gradient(135deg, #6c5ce7, ${planColor})`, borderRadius: 18, padding: 20, marginBottom: 14, display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ width: 60, height: 60, borderRadius: 16, background: user?.avatarUrl ? `url(${user.avatarUrl}) center/cover` : "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, overflow: "hidden", border: "2px solid rgba(255,255,255,0.3)" }}>{!user?.avatarUrl && "👤"}</div>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: "#fff" }}>{user?.name}</div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.8)", marginTop: 2 }}>{planLabel} · ★ {user?.trustScore || 0}</div>
            {user?.bio && <div style={{ fontSize: 11, color: "rgba(255,255,255,0.7)", marginTop: 4 }}>{user.bio}</div>}
          </div>
        </div>
        {/* Info rows */}
        <div style={{ background: c.card, borderRadius: 16, border: `1px solid ${c.border}`, padding: "4px 16px", marginBottom: 14 }}>
          {rows.map((item, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "13px 0", borderBottom: i < rows.length - 1 ? `1px solid ${c.border}` : "none" }}>
              <span style={{ fontSize: 13, color: c.sub, fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}><span>{item.icon}</span>{item.l}</span>
              <span style={{ fontSize: 13, color: c.text, fontWeight: 700 }}>{item.v}</span>
            </div>
          ))}
        </div>
        <Btn primary full onClick={() => go("edit_profile")}>Edit Profile</Btn>
      </div>
    </Shell>
  );
};

// ── PRIVACY PAGE ──
export const PrivacyPage = ({ privacySettings, setPrivacySettings }) => {
  const { c, go } = useApp();
  const items = [
    { l: "Profile visibility", d: "Who can see your profile", v: privacySettings.profileVisibility, opts: ["everyone", "members", "nobody"], k: "profileVisibility" },
    { l: "Show trust score", d: "Display your trust score publicly", toggle: true, on: privacySettings.showTrust, k: "showTrust" },
    { l: "Allow DMs", d: "Who can message you", v: privacySettings.allowDMs, opts: ["everyone", "verified", "nobody"], k: "allowDMs" },
    { l: "Show online status", d: "Let others see when you're active", toggle: true, on: privacySettings.showOnline, k: "showOnline" },
    { l: "Show activity", d: "Show likes and comments publicly", toggle: true, on: privacySettings.showActivity, k: "showActivity" },
  ];
  return (
    <Shell title="Privacy" showBack onBack={() => go("feed")}>
      <div style={{ padding: "16px 0" }}>
        <div style={{ background: c.card, borderRadius: 16, border: `1px solid ${c.border}`, padding: "4px 16px" }}>
          {items.map((item, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 0", borderBottom: i < items.length - 1 ? `1px solid ${c.border}` : "none" }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: c.text }}>{item.l}</div>
                <div style={{ fontSize: 11, color: c.faint, marginTop: 2 }}>{item.d}</div>
              </div>
              {item.toggle
                ? <Toggle on={item.on} onToggle={() => setPrivacySettings({ ...privacySettings, [item.k]: !item.on })} />
                : <select value={item.v} onChange={e => setPrivacySettings({ ...privacySettings, [item.k]: e.target.value })} style={{ padding: "6px 12px", borderRadius: 8, border: `1px solid ${c.border}`, background: c.input, color: c.text, fontSize: 12, fontWeight: 600, outline: "none" }}>
                    {item.opts.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
              }
            </div>
          ))}
        </div>
      </div>
    </Shell>
  );
};

// ── NOTIFICATIONS PAGE ──
export const NotificationsSettingsPage = ({ notifSettings, setNotifSettings }) => {
  const { c, go } = useApp();
  const items = [
    { l: "Push notifications", d: "Receive push on device", k: "push" },
    { l: "Email notifications", d: "Get updates via email", k: "email" },
    { l: "Likes", d: "When someone likes your post", k: "likes" },
    { l: "Comments", d: "When someone comments", k: "comments" },
    { l: "New followers", d: "When someone follows you", k: "follows" },
    { l: "Mentions", d: "When you're mentioned", k: "mentions" },
    { l: "Messages", d: "New direct messages", k: "messages" },
  ];
  return (
    <Shell title="Notifications" showBack onBack={() => go("feed")}>
      <div style={{ padding: "16px 0" }}>
        <div style={{ background: c.card, borderRadius: 16, border: `1px solid ${c.border}`, padding: "4px 16px" }}>
          {items.map((item, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 0", borderBottom: i < items.length - 1 ? `1px solid ${c.border}` : "none" }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: c.text }}>{item.l}</div>
                <div style={{ fontSize: 11, color: c.faint, marginTop: 2 }}>{item.d}</div>
              </div>
              <Toggle on={notifSettings[item.k]} onToggle={() => setNotifSettings({ ...notifSettings, [item.k]: !notifSettings[item.k] })} />
            </div>
          ))}
        </div>
      </div>
    </Shell>
  );
};

// ── APPEARANCE PAGE ──
export const AppearancePage = () => {
  const { c, dark, setDark, go } = useApp();
  return (
    <Shell title="Appearance" showBack onBack={() => go("feed")}>
      <div style={{ padding: "16px 0" }}>
        <div style={{ background: c.card, borderRadius: 16, border: `1px solid ${c.border}`, padding: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: c.sub, marginBottom: 14, letterSpacing: "0.05em" }}>THEME</div>
          <div style={{ display: "flex", gap: 12 }}>
            {[
              { l: "Dark", d: true, bg: "#0b0c10", preview: "#181a24", border: "#252840" },
              { l: "Light", d: false, bg: "#f4f5f9", preview: "#ffffff", border: "#e0e2eb" },
            ].map(th => (
              <button key={th.l} onClick={() => setDark(th.d)} style={{ flex: 1, padding: 16, borderRadius: 14, cursor: "pointer", border: `2px solid ${dark === th.d ? c.pri : c.border}`, background: th.bg, transition: "all 0.2s" }}>
                <div style={{ height: 40, borderRadius: 8, marginBottom: 8, background: th.preview, border: `1px solid ${th.border}` }} />
                <div style={{ fontSize: 13, fontWeight: 700, textAlign: "center", color: dark === th.d ? c.pri : "#888" }}>{th.l}</div>
                {dark === th.d && <div style={{ fontSize: 10, textAlign: "center", color: c.pri, marginTop: 4 }}>✓ Active</div>}
              </button>
            ))}
          </div>
        </div>
      </div>
    </Shell>
  );
};

// ── SECURITY PAGE ──
export const SecurityPage = () => {
  const { c, go, user, flash } = useApp();
  const [verifStatus, setVerifStatus] = useState(null);
  const [verifForm, setVerifForm] = useState({ instagram: "", twitter: "", facebook: "" });
  const [showVerifForm, setShowVerifForm] = useState(false);
  const [verifLoading, setVerifLoading] = useState(false);
  useEffect(() => {
    sessionStorage.removeItem("gc_settings_tab");
    if (!user?.is_verified && !user?.verified) {
      api("/verification/status").then(res => { if (res.ok) setVerifStatus(res.data?.request); });
    }
  }, []);
  const submitVerification = async () => {
    const filled = [verifForm.instagram, verifForm.twitter, verifForm.facebook].filter(v => v.trim()).length;
    if (filled < 2) { flash("Please provide at least 2 social media accounts", "error"); return; }
    setVerifLoading(true);
    const res = await api("/verification/submit", { method: "POST", body: JSON.stringify(verifForm) });
    setVerifLoading(false);
    if (res.ok) { flash("Submitted! Send 3 photos via WhatsApp 📱"); setVerifStatus(res.data?.request); setShowVerifForm(false); }
    else flash(res.message || "Failed", "error");
  };
  const isVerified = user?.verified || user?.is_verified;
  return (
    <Shell title="Security & Verification" showBack onBack={() => go("feed")}>
      <div style={{ padding: "16px 0" }}>
        {isVerified ? (
          <div style={{ background: c.grnBg, borderRadius: 16, border: `1px solid ${c.grn}25`, padding: 18, marginBottom: 12, display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ fontSize: 28 }}>✅</div>
            <div><div style={{ fontSize: 15, fontWeight: 700, color: c.grnTxt }}>Verified Account</div><div style={{ fontSize: 12, color: c.sub }}>Your identity has been confirmed</div></div>
          </div>
        ) : verifStatus?.status === "pending" ? (
          <div style={{ background: c.goldBg, borderRadius: 16, border: `1px solid ${c.gold}25`, padding: 18, marginBottom: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}><div style={{ fontSize: 24 }}>⏳</div><div><div style={{ fontSize: 15, fontWeight: 700, color: c.goldTxt }}>Pending Review</div><div style={{ fontSize: 12, color: c.sub }}>We're reviewing your request</div></div></div>
            <a href={`https://wa.me/254798515610?text=Hi%2C%20I%20am%20${encodeURIComponent(user?.name || "")}%20following%20up%20on%20my%20verification.`} target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 10, background: "#25D366", color: "#fff", fontSize: 12, fontWeight: 700, textDecoration: "none" }}>📱 Send Photos via WhatsApp</a>
          </div>
        ) : !showVerifForm ? (
          <div style={{ background: c.priBg, borderRadius: 16, border: `1px solid ${c.priBrd}`, padding: 20, marginBottom: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}><div style={{ fontSize: 28 }}>🛡️</div><div><div style={{ fontSize: 15, fontWeight: 700, color: c.priTxt }}>Get Verified</div><div style={{ fontSize: 12, color: c.sub }}>Unlock meetups, trust badge & more</div></div></div>
            <Btn primary full onClick={() => setShowVerifForm(true)}>Start Verification →</Btn>
          </div>
        ) : (
          <div style={{ background: c.card, borderRadius: 16, border: `2px solid ${c.priBrd}`, padding: 20, marginBottom: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <span style={{ fontSize: 15, fontWeight: 700, color: c.text }}>🛡️ Verify Your Identity</span>
              <button onClick={() => setShowVerifForm(false)} style={{ background: "none", border: "none", color: c.faint, cursor: "pointer", fontSize: 18 }}>✕</button>
            </div>
            <div style={{ fontSize: 12, color: c.sub, marginBottom: 14, lineHeight: 1.6, background: c.input, borderRadius: 10, padding: 12 }}>
              <b>Step 1:</b> Provide at least 2 social accounts below.<br />
              <b>Step 2:</b> <a href={`https://wa.me/254798515610?text=Hi%20I%20am%20${encodeURIComponent(user?.name || "")}%20requesting%20verification`} target="_blank" rel="noopener noreferrer" style={{ color: "#25D366", fontWeight: 700 }}>Send 3 photos via WhatsApp →</a>
            </div>
            {[{ key: "instagram", icon: "📸", label: "INSTAGRAM", placeholder: "@your_username" }, { key: "twitter", icon: "🐦", label: "X / TWITTER", placeholder: "@your_handle" }, { key: "facebook", icon: "📘", label: "FACEBOOK", placeholder: "https://facebook.com/yourprofile" }].map(f => (
              <div key={f.key} style={{ marginBottom: 12 }}>
                <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: c.sub, marginBottom: 5 }}>{f.icon} {f.label}</label>
                <input style={{ width: "100%", padding: "11px 14px", borderRadius: 11, border: `1.5px solid ${c.border}`, background: c.input, color: c.text, fontSize: 14, outline: "none", boxSizing: "border-box", fontFamily: "inherit" }} placeholder={f.placeholder} value={verifForm[f.key]} onChange={e => setVerifForm({ ...verifForm, [f.key]: e.target.value })} />
              </div>
            ))}
            <Btn primary full onClick={submitVerification} disabled={verifLoading}>{verifLoading ? "Submitting..." : "Submit Verification Request"}</Btn>
          </div>
        )}
        <div style={{ background: c.card, borderRadius: 16, border: `1px solid ${c.border}`, padding: "4px 16px" }}>
          {[{ l: "Change Password", d: "Update your account password", a: "Change" }, { l: "Two-Factor Auth", d: "Add extra security layer", a: "Enable" }, { l: "Active Sessions", d: "View and manage sessions", a: "View" }, { l: "Login History", d: "See recent login activity", a: "View" }].map((item, i, arr) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 0", borderBottom: i < arr.length - 1 ? `1px solid ${c.border}` : "none" }}>
              <div><div style={{ fontSize: 14, fontWeight: 600, color: c.text }}>{item.l}</div><div style={{ fontSize: 11, color: c.faint, marginTop: 2 }}>{item.d}</div></div>
              <Btn small onClick={() => flash("Coming soon!")}>{item.a}</Btn>
            </div>
          ))}
        </div>
      </div>
    </Shell>
  );
};

// ── BLOCKED USERS PAGE ──
export const BlockedUsersPage = ({ blockedUsers, setBlockedUsers, getAuthorName }) => {
  const { c, go, flash } = useApp();
  const [blocked, setBlocked] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api("/blocked-users").then(res => {
      if (res.ok && Array.isArray(res.data)) {
        setBlocked(res.data.map(u => ({
          id: u.id,
          name: u.name,
          emoji: u.avatar_emoji || "👤",
          avatar: u.avatar_path ? `https://community.geilstore.com/storage/${u.avatar_path}` : null,
          plan: u.plan || "free",
          verified: u.is_verified || false,
        })));
      }
      setLoading(false);
    });
  }, []);

  const doUnblock = async (userId, userName) => {
    const res = await api(`/profile/${userId}/block`, { method: "POST" });
    if (res.ok) {
      setBlocked(prev => prev.filter(u => u.id !== userId));
      flash(`${userName} unblocked ✅`);
      // Clear cached viewing user data so profile refreshes on next visit
      sessionStorage.removeItem("gc_viewing_user");
    } else flash("Failed to unblock", "error");
  };

  return (
    <Shell title="Blocked Users" showBack onBack={() => go("settings")}>
      <div style={{ padding: "16px 0" }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: 40, color: c.sub, fontSize: 13 }}>Loading...</div>
        ) : blocked.length === 0 ? (
          <EmptyState icon="🚫" title="No blocked users" desc="Users you block will appear here" />
        ) : (
          <div style={{ background: c.card, borderRadius: 16, border: `1px solid ${c.border}`, padding: "4px 16px" }}>
            {blocked.map((u, i) => (
              <div key={u.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0", borderBottom: i < blocked.length - 1 ? `1px solid ${c.border}` : "none" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 40, height: 40, borderRadius: 12, background: u.avatar ? `url(${u.avatar}) center/cover` : "linear-gradient(135deg,#6c5ce7,#e91e8c)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, overflow: "hidden", flexShrink: 0 }}>
                    {!u.avatar && u.emoji}
                  </div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: c.text }}>{u.name}</div>
                    <div style={{ fontSize: 11, color: c.sub }}>{u.plan} · {u.verified ? "✓ Verified" : "Unverified"}</div>
                  </div>
                </div>
                <Btn small onClick={() => doUnblock(u.id, u.name)}>Unblock</Btn>
              </div>
            ))}
          </div>
        )}
      </div>
    </Shell>
  );
};

// ── DATA & PRIVACY PAGE ──
export const DataPrivacyPage = () => {
  const { c, go, flash } = useApp();
  return (
    <Shell title="Data & Privacy" showBack onBack={() => go("feed")}>
      <div style={{ padding: "16px 0" }}>
        <div style={{ background: c.card, borderRadius: 16, border: `1px solid ${c.border}`, padding: "4px 16px" }}>
          {[
            { l: "Export your data", d: "Download all your data", a: "Download", fn: () => flash("Data export coming soon") },
            { l: "Request data deletion", d: "Permanently delete all data", a: "Request", fn: () => flash("Contact support to delete data") },
            { l: "Privacy Policy", d: "Read our privacy policy", a: "View", fn: () => window.open("https://community.geilstore.com/privacy", "_blank") },
            { l: "Terms of Service", d: "Read our terms", a: "View", fn: () => window.open("https://community.geilstore.com/terms", "_blank") },
          ].map((item, i, arr) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 0", borderBottom: i < arr.length - 1 ? `1px solid ${c.border}` : "none" }}>
              <div><div style={{ fontSize: 14, fontWeight: 600, color: c.text }}>{item.l}</div><div style={{ fontSize: 11, color: c.faint, marginTop: 2 }}>{item.d}</div></div>
              <Btn small onClick={item.fn}>{item.a}</Btn>
            </div>
          ))}
        </div>
      </div>
    </Shell>
  );
};

// ── DEFAULT SETTINGS PAGE (kept as redirect hub) ──
const SettingsPage = ({ settingsTab, setSettingsTab, profileForm, setProfileForm, handleProfilePicUpload, fileRef, privacySettings, setPrivacySettings, notifSettings, setNotifSettings, blockedUsers, setBlockedUsers, getAuthorName }) => {
  const { c, go } = useApp();
  useEffect(() => {
    const t = sessionStorage.getItem("gc_settings_tab");
    if (t) {
      sessionStorage.removeItem("gc_settings_tab");
      const map = { account: "edit_profile", privacy: "settings_privacy", notifs: "settings_notifs", appearance: "settings_appearance", security: "settings_security", blocked: "settings_blocked", data: "settings_data" };
      if (map[t]) { go(map[t]); return; }
    }
  }, []);
  const MenuItem = ({ icon, label, desc, color, onClick }) => (
    <button onClick={onClick} style={{ display: "flex", alignItems: "center", gap: 14, width: "100%", padding: "14px 16px", borderRadius: 14, border: `1px solid ${c.border}`, background: c.card, cursor: "pointer", textAlign: "left", marginBottom: 10 }}>
      <div style={{ width: 42, height: 42, borderRadius: 12, background: `${color || c.pri}15`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>{icon}</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: color || c.text }}>{label}</div>
        {desc && <div style={{ fontSize: 11, color: c.faint, marginTop: 2 }}>{desc}</div>}
      </div>
      <span style={{ fontSize: 18, color: c.faint }}>›</span>
    </button>
  );
  return (
    <Shell title="Settings" showBack onBack={() => go("feed")}>
      <div style={{ padding: "16px 0" }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: c.faint, letterSpacing: "0.08em", marginBottom: 10, paddingLeft: 4 }}>MY ACCOUNT</div>
        <MenuItem icon="✏️" label="Edit Profile" desc="Name, bio, photo, location" color="#6c5ce7" onClick={() => go("edit_profile")} />
        <div style={{ fontSize: 11, fontWeight: 700, color: c.faint, letterSpacing: "0.08em", marginBottom: 10, marginTop: 8, paddingLeft: 4 }}>SETTINGS</div>
        <MenuItem icon="🔒" label="Privacy" desc="DMs, visibility, online status" color="#3b82f6" onClick={() => go("settings_privacy")} />
        <MenuItem icon="🔔" label="Notifications" desc="Push, email, alerts" color="#f59e0b" onClick={() => go("settings_notifs")} />
        <MenuItem icon="🎨" label="Appearance" desc="Dark or light theme" color="#8b5cf6" onClick={() => go("settings_appearance")} />
        <MenuItem icon="🛡️" label="Security & Verification" desc="Password, 2FA, verification" color="#10b981" onClick={() => go("settings_security")} />
        <MenuItem icon="🚫" label="Blocked Users" desc="Manage blocked accounts" color="#ef4444" onClick={() => go("settings_blocked")} />
        <MenuItem icon="📊" label="Data & Privacy" desc="Export, delete, policies" color="#6b7280" onClick={() => go("settings_data")} />
      </div>
    </Shell>
  );
};

export default SettingsPage;
